import type { BannerUploadData } from './types';
import { DeviceType, detectDeviceTypeFromFilename, detectDeviceTypeFromDimensions } from './types';

export interface BannerService {
  uploadFile: (file: File) => Promise<string | null>;
  removeUploadingFile: (fileName: string) => void;
  uploadingFiles: Record<string, { name: string; progress: number; error?: string }>;
}

export const BANNER_PC_IMAGE_MAX_BYTES = 100 * 1024;
export const BANNER_MOBILE_IMAGE_MAX_BYTES = 60 * 1024;
export const BANNER_VIDEO_MAX_BYTES = 1024 * 1024;

export const formatKb = (bytes: number) => `${Math.round(bytes / 1024)}KB`;
export const formatMb = (bytes: number) => `${Math.round(bytes / 1024 / 1024)}MB`;

export const validateBannerFileSize = (file: File, deviceType: DeviceType): void => {
  if (file.type === 'video/mp4') {
    if (file.size > BANNER_VIDEO_MAX_BYTES) {
      throw new Error(
        `${file.name}: video size ${formatMb(file.size)} exceeds the ${formatMb(BANNER_VIDEO_MAX_BYTES)} limit.`,
      );
    }
    return;
  }

  if (deviceType === DeviceType.MOBILE) {
    if (file.size > BANNER_MOBILE_IMAGE_MAX_BYTES) {
      throw new Error(
        `${file.name}: mobile banner size ${formatKb(file.size)} exceeds the ${formatKb(BANNER_MOBILE_IMAGE_MAX_BYTES)} limit.`,
      );
    }
    return;
  }

  if (file.size > BANNER_PC_IMAGE_MAX_BYTES) {
    throw new Error(
      `${file.name}: PC banner size ${formatKb(file.size)} exceeds the ${formatKb(BANNER_PC_IMAGE_MAX_BYTES)} limit.`,
    );
  }
};

/**
 * Process a file, detect its type, upload to S3, and add to the appropriate banner group
 */
export const processFile = async (
  file: File,
  banner: BannerService,
  uploadedFiles: BannerUploadData[],
  setUploadedFiles: React.Dispatch<React.SetStateAction<BannerUploadData[]>>,
  dateRange: { fromDate: Date; toDate: Date },
): Promise<void> => {
  // 1. Determine the device type from filename
  let deviceType = detectDeviceTypeFromFilename(file.name);

  // 2. If no clear match from filename, check actual image dimensions
  if (!deviceType) {
    deviceType = await detectDeviceTypeFromDimensions();
  }

  // 3. Enforce size limits per device type / media kind
  validateBannerFileSize(file, deviceType);

  // 4. Extract base name (remove dimensions and extension)
  const baseName = file.name.replace(/_\d+x\d+\.\w+$/, '').replace(/\.\w+$/, '');

  // 5. Upload the file to S3
  const s3Url = await banner.uploadFile(file);
  if (!s3Url) {
    throw new Error(`Failed to upload ${file.name}`);
  }

  console.info(`Uploaded ${file.name} as ${deviceType} image to ${s3Url}`);

  // 5. Find if we already have a banner group with this base name
  const existingGroupIndex = uploadedFiles.findIndex(
    (item) => item.title.toLowerCase() === baseName.replace(/_/g, ' ').trim().toLowerCase(),
  );

  if (existingGroupIndex >= 0) {
    // Add this image to an existing banner group
    setUploadedFiles((prev) => {
      const updatedFiles = [...prev];
      const currentGroup = updatedFiles[existingGroupIndex];

      if (!currentGroup) {
        return prev;
      } // Guard against undefined

      // Ensure imageFiles is initialized
      if (!currentGroup.imageFiles) {
        currentGroup.imageFiles = {};
      }

      // Update the image URL based on device type
      if (deviceType === DeviceType.DESKTOP) {
        currentGroup.imageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.DESKTOP] = file;
      } else if (deviceType === DeviceType.TABLET) {
        currentGroup.tabletImageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.TABLET] = file;
      } else if (deviceType === DeviceType.MOBILE) {
        currentGroup.mobileImageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.MOBILE] = file;
      }

      return updatedFiles;
    });
  } else {
    // Create a new banner group
    const newBanner: BannerUploadData = {
      title: baseName.replace(/_/g, ' ').trim(),
      description: '',
      linkUrl: '',
      location: '',
      active: true,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      imageFiles: {},
    };

    // Set the appropriate image URL based on device type
    if (deviceType === DeviceType.DESKTOP) {
      newBanner.imageUrl = s3Url;
      newBanner.imageFiles[DeviceType.DESKTOP] = file;
    } else if (deviceType === DeviceType.TABLET) {
      newBanner.tabletImageUrl = s3Url;
      newBanner.imageFiles[DeviceType.TABLET] = file;
    } else if (deviceType === DeviceType.MOBILE) {
      newBanner.mobileImageUrl = s3Url;
      newBanner.imageFiles[DeviceType.MOBILE] = file;
    }

    setUploadedFiles((prev) => [...prev, newBanner]);
  }
};
